import type { ReactNode } from 'react';
import { SYSTEM_ROLES } from '../../constants/system';
import { useIsSingleTenant } from '../../hooks/organization';
import translations from '../../translations';
import DropdownList from './DropdownList';
import TenantDropdown from './TenantDropdown';
import DropdownButton from './headerDropdown/DropdownButton';
import DropdownListButton from './headerDropdown/DropdownListButton';
import DropdownListLink from './headerDropdown/DropdownListLink';
import type { UserTenantResponseAPI } from '../../types/user';

const appLinks = {
    integration: 'https://platform.boomi.com/AtomSphere.html',
    dataHub: 'https://platform.boomi.com/MdmSphere.html',
    apiManagement: 'https://platform.boomi.com/ApiSphere.html',
    taskAutomation: 'https://taskautomation.boomi.com',
    eventStreams: 'https://platform.boomi.com/EventStreams.html',
} as const;

const helpLinks = {
    docs: 'https://help.boomi.com/category/flow',
    support: 'https://community.boomi.com/s/support',
    community: 'https://community.boomi.com/s/topic/0TO1W000000cffIWAQ/flow',
    training: 'https://train.boomi.com/',
    marketplace: 'https://marketplace.boomi.com/browse-solutions?category=flow',
    billing: 'https://billing.stripe.com/p/login/aEU6s02oi0Gw7IIcMM',
} as const;

const labsLinks = {
    spaces: 'https://boomi.com/boomi-labs/#spaces',
    bundles: 'https://platform.boomi.com/BoomiLabs.html#pub_bundles',
    packs: 'https://platform.boomi.com/BoomiLabs.html#pub_ipacks',
    processLib: 'https://platform.boomi.com/BoomiLabs.html#pub_pl',
    connectors: 'https://platform.boomi.com/AtomSphere.html#settings',
} as const;

const settingsScreens = {
    orgs: 'organization',
    tenant: 'tenant',
    environments: 'environments',
} as const;

interface Link {
    text: string;
    summary: string;
    action:
        | (typeof appLinks)[keyof typeof appLinks]
        | (typeof helpLinks)[keyof typeof helpLinks]
        | (typeof labsLinks)[keyof typeof labsLinks];
}

interface HeaderNavSection {
    id: string;
    listClass: string;
    buttonContents: ReactNode;
    links: Link[];
    buttons: {
        text: string;
        summary: string;
        screen: (typeof settingsScreens)[keyof typeof settingsScreens];
        icon: string;
        visible: boolean;
    }[];
    icon: boolean;
}

interface HeaderNavProps {
    openTenantSelector: () => void;
    tenant: UserTenantResponseAPI;
}

const HeaderNav = ({ openTenantSelector, tenant }: HeaderNavProps) => {
    const isSingleTenant = useIsSingleTenant(tenant);

    let isAdministrator = tenant.role === SYSTEM_ROLES.administrator.developerName;

    if (isSingleTenant) {
        if (
            tenant.role === SYSTEM_ROLES.administrator.developerName ||
            tenant.organization?.role === 'organization_administrator'
        ) {
            isAdministrator = true;
        }
    }

    const showEnvironmentMenuItem = !!(tenant.tenantSettings?.environments && isAdministrator);

    // In "Account mode" (single-tenant organizations with the ORGACC flag on) the tenant concept
    // is hidden behind a single "Account" settings screen, so the separate Organization entry is
    // dropped and the Tenant entry becomes "Account".
    const accountMode = isSingleTenant;

    const orgButton = {
        text: translations.HEADER_org_link_text,
        summary: translations.HEADER_org_summary_text,
        screen: settingsScreens.orgs,
        icon: 'Organization',
        visible: true,
    };
    const tenantButton = {
        text: translations.HEADER_tenant_link_text,
        summary: translations.HEADER_tenant_summary_text,
        screen: settingsScreens.tenant,
        icon: 'Tenant',
        visible: true,
    };
    const accountButton = {
        text: translations.HEADER_account_link_text,
        summary: translations.HEADER_account_summary_text,
        screen: settingsScreens.tenant,
        icon: 'Tenant',
        visible: true,
    };
    const environmentsButton = {
        text: translations.HEADER_environments_link_text,
        summary: translations.HEADER_environments_summary_text,
        screen: settingsScreens.environments,
        icon: 'Environments',
        visible: showEnvironmentMenuItem,
    };

    // In Account mode show a single "Account" entry; otherwise the separate Organization and
    // Tenant entries.
    const getSettingsButtons = () => {
        if (accountMode) {
            return [accountButton, environmentsButton];
        }

        return [orgButton, tenantButton, environmentsButton];
    };

    const servicesLinks: Link[] = [
        {
            text: translations.HEADER_integration_link_text,
            summary: translations.HEADER_integration_summary_text,
            action: appLinks.integration,
        },
        {
            text: translations.HEADER_datahub_link_text,
            summary: translations.HEADER_datahub_summary_text,
            action: appLinks.dataHub,
        },
        {
            text: translations.HEADER_apim_link_text,
            summary: translations.HEADER_apim_summary_text,
            action: appLinks.apiManagement,
        },
        {
            text: translations.HEADER_event_streams_link_text,
            summary: translations.HEADER_event_streams_summary_text,
            action: appLinks.eventStreams,
        },
        {
            text: translations.HEADER_auto_link_text,
            summary: translations.HEADER_auto_summary_text,
            action: appLinks.taskAutomation,
        },
    ];

    const headerNavConfig: HeaderNavSection[] = [
        {
            id: 'boomi-services-list',
            listClass: 'boomi-services-list',
            buttonContents: <DropdownButton buttonText={translations.HEADER_services} />,
            links: servicesLinks,
            buttons: [],
            icon: true,
        },
        {
            id: 'help-dropdown-list',
            listClass: 'help-dropdown-list',
            buttonContents: <DropdownButton buttonText={translations.HEADER_resources} />,
            links: [
                {
                    text: translations.HEADER_docs_link_text,
                    summary: translations.HEADER_docs_summary_text,
                    action: helpLinks.docs,
                },
                {
                    text: translations.HEADER_support_link_text,
                    summary: translations.HEADER_support_summary_text,
                    action: helpLinks.support,
                },
                {
                    text: translations.HEADER_community_link_text,
                    summary: translations.HEADER_community_summary_text,
                    action: helpLinks.community,
                },
                {
                    text: translations.HEADER_training_link_text,
                    summary: translations.HEADER_training_summary_text,
                    action: helpLinks.training,
                },
                {
                    text: translations.HEADER_marketplace_link_text,
                    summary: translations.HEADER_marketplace_summary_text,
                    action: helpLinks.marketplace,
                },
                {
                    text: translations.HEADER_billing_link_text,
                    summary: translations.HEADER_billing_summary_text,
                    action: helpLinks.billing,
                },
            ],
            buttons: [],
            icon: true,
        },
        {
            id: 'labs-dropdown-list',
            listClass: 'labs-dropdown-list',
            buttonContents: <DropdownButton buttonText={translations.HEADER_labs} />,
            links: [
                {
                    text: translations.HEADER_spaces_link_text,
                    summary: translations.HEADER_spaces_summary_text,
                    action: helpLinks.support,
                },
                {
                    text: translations.HEADER_bundles_link_text,
                    summary: translations.HEADER_bundles_summary_text,
                    action: labsLinks.bundles,
                },
                {
                    text: translations.HEADER_packs_link_text,
                    summary: translations.HEADER_packs_summary_text,
                    action: labsLinks.packs,
                },
                {
                    text: translations.HEADER_process_lib_link_text,
                    summary: translations.HEADER_process_lib_summary_text,
                    action: labsLinks.processLib,
                },
                {
                    text: translations.HEADER_connectors_link_text,
                    summary: translations.HEADER_connectors_summary_text,
                    action: labsLinks.connectors,
                },
            ],
            buttons: [],
            icon: true,
        },
        {
            id: 'settings-dropdown-list',
            listClass: 'settings-dropdown-list',
            buttonContents: <DropdownButton buttonText={translations.HEADER_settings} />,
            links: [],
            buttons: getSettingsButtons(),
            icon: false,
        },
    ];

    if (!isAdministrator) {
        headerNavConfig.pop();
    }

    return (
        <nav className="tab-header-admin header-admin-left">
            {headerNavConfig.map(({ id, listClass, links, buttonContents, buttons, icon }) => (
                <DropdownList key={id} alignRight={false} buttonContents={buttonContents}>
                    <div className={`list-unstyled dropdown-list ${listClass}`}>
                        {links.map(({ text, summary, action }) => (
                            <DropdownListLink
                                key={text}
                                linkText={text}
                                linkSummary={summary}
                                linkHref={action}
                                hasIcon={icon}
                                rel="noopener noreferrer"
                                target="_blank"
                            />
                        ))}
                        {buttons.map(({ text, screen, summary, icon, visible }) => (
                            <DropdownListButton
                                key={text}
                                navigateTo={screen}
                                buttonText={text}
                                buttonSummary={summary}
                                icon={icon}
                                visible={visible}
                            />
                        ))}
                    </div>
                </DropdownList>
            ))}
            <TenantDropdown tenant={tenant} openTenantSelector={openTenantSelector} />
        </nav>
    );
};

export default HeaderNav;
